/**
 * BarometerFinder – Geführter Fragebogen
 * 3 Fragen → Empfehlung für das passende Barometer oder Bundle
 */
import { useState } from "react";

interface Question {
  id: string;
  text: string;
  options: { value: string; label: string; icon: string }[];
}

interface Recommendation {
  title: string;
  desc: string;
  href: string;
  barometer: string[];
  cta: string;
  accent: string;
}

const QUESTIONS: Question[] = [
  {
    id: "goal",
    text: "Was ist dein wichtigstes Ziel gerade?",
    options: [
      { value: "visibility", label: "Mehr Sichtbarkeit bei Google & KI", icon: "/manus-storage/23_1ab7147e.svg" },
      { value: "conversion", label: "Mehr Anfragen & Verkäufe", icon: "/manus-storage/33_d51b7819.svg" },
      { value: "compliance", label: "Rechtssicherheit & Barrierefreiheit", icon: "/manus-storage/29_7f3bb394.svg" },
      { value: "overview", label: "Vollständiges Bild meines Marketings", icon: "/manus-storage/32_04885248.svg" },
    ],
  },
  {
    id: "type",
    text: "Was beschreibt dein Unternehmen am besten?",
    options: [
      { value: "local", label: "Lokales Unternehmen / Dienstleister", icon: "/manus-storage/1_55991aa3.svg" },
      { value: "ecommerce", label: "Online-Shop / E-Commerce", icon: "/manus-storage/21_453003f0.svg" },
      { value: "b2b", label: "B2B / Agentur / Beratung", icon: "/manus-storage/47_7e4350f7.svg" },
      { value: "startup", label: "Startup / Neugründung", icon: "/manus-storage/icon_startup_306e4fc0.png" },
    ],
  },
  {
    id: "urgency",
    text: "Was ist dein dringlichstes Problem?",
    options: [
      { value: "notfound", label: "Kunden finden mich nicht online", icon: "/manus-storage/7_8bcad2d2.svg" },
      { value: "bounce", label: "Besucher kommen, kaufen aber nicht", icon: "/manus-storage/icon_cta_value_9a48b0a2.png" },
      { value: "legal", label: "Ich bin unsicher bei DSGVO / EAA", icon: "/manus-storage/29_7f3bb394.svg" },
      { value: "competition", label: "Konkurrenz ist sichtbarer als ich", icon: "/manus-storage/41_3269a7aa.svg" },
    ],
  },
];

// Empfehlungslogik
function getRecommendation(answers: Record<string, string>): Recommendation {
  const { goal, type, urgency } = answers;

  // Vollständiges Bild → Komplett-Paket
  if (goal === "overview") {
    return {
      title: "Komplett Website-RealitätsCheck™",
      desc: "Du willst das vollständige Bild – alle 9 Barometer analysieren deine gesamte Online-Präsenz.",
      href: "/komplett-paket",
      barometer: ["Alle 9 Barometer"],
      cta: "Zum Komplett Website-RealitätsCheck™",
      accent: "#6d0000",
    };
  }

  // Lokales Unternehmen + Sichtbarkeit
  if (type === "local" && (goal === "visibility" || urgency === "notfound")) {
    return {
      title: "Basic Website-RealitätsCheck™",
      desc: "Für lokale Unternehmen: SEO-Grundlagen, lokale Sichtbarkeit, Datenschutz und Barrierefreiheit in einem Paket.",
      href: "/website-realitaetscheck",
      barometer: ["SEO", "Lokal", "Accessibility", "Datenschutz"],
      cta: "Zum Basic Website-RealitätsCheck™",
      accent: "#115751",
    };
  }

  // E-Commerce + Conversion
  if (type === "ecommerce" && (goal === "conversion" || urgency === "bounce")) {
    return {
      title: "E-Commerce + Conversion",
      desc: "Dein Shop verliert Kunden? E-Commerce-Barometer analysiert deinen Checkout, Conversion-Barometer zeigt psychologische Hebel.",
      href: "/mein-paket",
      barometer: ["E-Commerce", "Conversion"],
      cta: "Paket zusammenstellen",
      accent: "#4f46e5",
    };
  }

  // Compliance-Ziel
  if (goal === "compliance" || urgency === "legal") {
    return {
      title: "Datenschutz + Accessibility",
      desc: "DSGVO-Konformität und European Accessibility Act (EAA) – ab 2025 Pflicht für viele Unternehmen.",
      href: "/mein-paket",
      barometer: ["Datenschutz", "Accessibility"],
      cta: "Paket zusammenstellen",
      accent: "#0f766e",
    };
  }

  // Konkurrenz / Wettbewerb
  if (urgency === "competition") {
    return {
      title: "GEO + Wettbewerb",
      desc: "KI-Sichtbarkeit bei ChatGPT & Co. plus direkter Wettbewerbsvergleich – wer ist sichtbarer und warum?",
      href: "/mein-paket",
      barometer: ["GEO / KI", "Wettbewerb"],
      cta: "Paket zusammenstellen",
      accent: "#1a3a5c",
    };
  }

  // B2B / Sichtbarkeit
  if (type === "b2b" && goal === "visibility") {
    return {
      title: "GEO + SEO + Wettbewerb",
      desc: "B2B-Kunden suchen bei Google und KI-Tools. Diese drei Barometer zeigen, warum du nicht gefunden wirst.",
      href: "/mein-paket",
      barometer: ["GEO / KI", "SEO", "Wettbewerb"],
      cta: "Paket zusammenstellen",
      accent: "#0a5c36",
    };
  }

  // Startup / Neugründung
  if (type === "startup") {
    return {
      title: "Pro Website-RealitätsCheck™",
      desc: "Als Startup brauchst du das vollständige Bild: Sensorik, GEO, SEO, Conversion, Lokal und mehr.",
      href: "/marketing-realitaetscheck",
      barometer: ["Sensorik", "GEO", "SEO", "Conversion", "Lokal", "Accessibility", "Datenschutz"],
      cta: "Zum Pro Website-RealitätsCheck™",
      accent: "#6d0000",
    };
  }

  // Default: Conversion-Fokus
  return {
    title: "Conversion-Barometer™",
    desc: "Besucher kommen, kaufen aber nicht? Das Conversion-Barometer analysiert CTAs, Vertrauen und Reibungspunkte.",
    href: "/conversion-barometer",
    barometer: ["Conversion"],
    cta: "Zum Conversion-Barometer™",
    accent: "#b45309",
  };
}

export function BarometerFinder() {
  const [step, setStep] = useState(0); // 0 = Intro, 1-3 = Fragen, 4 = Ergebnis
  const [answers, setAnswers] = useState<Record<string, string>>({});

  const handleAnswer = (questionId: string, value: string) => {
    const newAnswers = { ...answers, [questionId]: value };
    setAnswers(newAnswers);
    if (step < 3) {
      setStep(step + 1);
    } else {
      setStep(4);
    }
  };

  const reset = () => {
    setStep(0);
    setAnswers({});
  };

  const recommendation = step === 4 ? getRecommendation(answers) : null;
  const currentQuestion = step >= 1 && step <= 3 ? QUESTIONS[step - 1] : null;

  return (
    <section className="w-full bg-black py-16 px-6" id="barometer-finder">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>
            Nicht sicher, wo du anfangen sollst?
          </p>
          <h2 className="font-black uppercase text-white leading-none mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2rem, 5vw, 3rem)" }}>
            BAROMETER-FINDER
          </h2>
          <p className="text-gray-400 text-sm max-w-xl mx-auto" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            3 kurze Fragen – und du weißt genau, welche Analyse dir am meisten bringt.
          </p>
        </div>

        {/* Fortschrittsanzeige */}
        {step >= 1 && step <= 3 && (
          <div className="flex items-center gap-2 justify-center mb-8">
            {[1, 2, 3].map(i => (
              <div key={i} className="flex items-center gap-2">
                <div
                  className="w-8 h-8 flex items-center justify-center font-black text-sm transition-all"
                  style={{
                    backgroundColor: i < step ? "#6d0000" : i === step ? "#fff" : "transparent",
                    color: i < step ? "#fff" : i === step ? "#000" : "#6b7280",
                    border: i === step ? "none" : "1px solid #374151",
                    fontFamily: "'Bebas Neue', sans-serif",
                  }}
                >
                  {i < step ? "✓" : i}
                </div>
                {i < 3 && <div className="w-8 h-px" style={{ backgroundColor: i < step ? "#6d0000" : "#374151" }} />}
              </div>
            ))}
          </div>
        )}

        {/* Intro */}
        {step === 0 && (
          <div className="text-center">
            <img src="/manus-storage/icon_cta_value_9a48b0a2.png" alt="" className="w-16 h-16 mx-auto mb-6 object-contain" style={{ opacity: 0.85 }} />
            <p className="text-gray-300 mb-8 max-w-lg mx-auto" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Beantworte 3 kurze Fragen und erhalte eine persönliche Empfehlung.
            </p>
            <button
              onClick={() => setStep(1)}
              className="px-8 py-4 font-black uppercase tracking-widest text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: "#6d0000", fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.1rem" }}
            >
              Jetzt starten →
            </button>
          </div>
        )}

        {/* Fragen */}
        {currentQuestion && (
          <div>
            <h3 className="font-black uppercase text-white text-center mb-6" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.3rem, 3vw, 1.8rem)" }}>
              {currentQuestion.text}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {currentQuestion.options.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => handleAnswer(currentQuestion.id, opt.value)}
                  className="text-left p-5 border border-gray-700 hover:border-gray-400 transition-all group"
                  style={{ backgroundColor: "transparent" }}
                >
                  <div className="flex items-center gap-3">
                    <img src={opt.icon} alt="" className="w-7 h-7 flex-shrink-0 object-contain" style={{ opacity: 0.8 }} />
                    <span className="text-white text-sm font-bold group-hover:text-gray-200 transition-colors" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                      {opt.label}
                    </span>
                  </div>
                </button>
              ))}
            </div>
            <button
              onClick={reset}
              className="mt-6 text-xs text-gray-600 hover:text-gray-400 transition-colors block mx-auto uppercase tracking-widest"
            >
              ← Neu starten
            </button>
          </div>
        )}

        {/* Ergebnis */}
        {step === 4 && recommendation && (
          <div className="border-2 p-8" style={{ borderColor: recommendation.accent }}>
            <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: recommendation.accent, fontFamily: "'DM Sans', sans-serif" }}>
              Meine Empfehlung für dich
            </p>
            <h3 className="font-black uppercase text-white mb-3 leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.8rem, 4vw, 2.5rem)" }}>
              {recommendation.title}
            </h3>
            <p className="text-gray-300 mb-5 text-sm leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              {recommendation.desc}
            </p>
            <div className="flex flex-wrap gap-2 mb-6">
              {recommendation.barometer.map(b => (
                <span key={b} className="text-xs font-bold px-3 py-1 text-white uppercase tracking-widest"
                  style={{ backgroundColor: recommendation.accent }}>
                  {b}
                </span>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={recommendation.href}
                className="px-6 py-3 font-black uppercase tracking-widest text-white text-center transition-opacity hover:opacity-90"
                style={{ backgroundColor: recommendation.accent, fontFamily: "'Bebas Neue', sans-serif", fontSize: "1rem", textDecoration: "none" }}
              >
                {recommendation.cta} →
              </a>
              <button
                onClick={reset}
                className="px-6 py-3 font-bold uppercase tracking-widest text-gray-400 border border-gray-700 hover:border-gray-500 transition-colors text-sm"
              >
                Neu starten
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
